// src/components/UpcomingEventsWidget.js
import React from 'react';
import { 
  Calendar, Clock, Plane, Fuel, Wrench, Users, 
  AlertCircle, ChevronRight, RefreshCw, Filter,
  CheckCircle, XCircle, DollarSign
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUpcomingNotifications } from '../manager_notifications';
import { getPriorityColorClass, getNotificationSummary, isUrgent } from '../manager_notifications';

const EVENT_TYPES = {
  flight: { label: 'Flight', icon: Plane, color: 'blue' },
  fuel: { label: 'Fuel', icon: Fuel, color: 'orange' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'red' },
  meeting: { label: 'Meeting', icon: Users, color: 'purple' },
  appointment: { label: 'Appointment', icon: Calendar, color: 'green' }
};

const UpcomingEventsWidget = ({ refreshTrigger = 0 }) => {
  const { hasMinLevel } = useAuth();
  const canManageEvents = hasMinLevel(20);
  const { notifications, loading, refresh } = useUpcomingNotifications(10);
  
  // Rafraîchir quand refreshTrigger change
  React.useEffect(() => {
    if (refreshTrigger) refresh();
  }, [refreshTrigger, refresh]);

  // Fonction pour naviguer vers le planning
  const navigateToSchedule = () => {
    window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'schedule' } }));
  };

  const getStatusIcon = (notification) => {
    if (notification.status === 'completed') {
      return <CheckCircle className="w-3 h-3 text-green-400" />;
    } else if (notification.status === 'in_progress') {
      return <Clock className="w-3 h-3 text-yellow-400" />;
    } else if (isUrgent(notification)) {
      return <AlertCircle className="w-3 h-3 text-red-400" />;
    }
    return <Clock className="w-3 h-3 text-gray-400" />;
  };

  const getEventStyle = (notification) => {
    const type = EVENT_TYPES[notification.type] || EVENT_TYPES.appointment;
    return {
      bgColor: `bg-${type.color}-500/20`,
      borderColor: `border-${type.color}-500`,
      icon: type.icon,
      label: type.label
    };
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const isToday = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const [expandedEvent, setExpandedEvent] = React.useState(null);

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-400" />
          <h3 className="text-white font-semibold">Upcoming Events</h3>
          <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded-full">
            {notifications.length}
          </span>
        </div>
        
        <div className="flex gap-2">
          <button onClick={refresh} className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No events scheduled</p>
            {canManageEvents && (
              <p className="text-xs text-gray-600 mt-1">Add events in Schedule tab</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {notifications.map((notification) => {
              const style = getEventStyle(notification);
              const EventIcon = style.icon;
              const isExpanded = expandedEvent === notification.id;
              const urgent = isUrgent(notification);
              
              return (
                <div
                  key={notification.id}
                  className={`p-3 hover:bg-gray-700/30 transition cursor-pointer ${
                    isExpanded ? 'bg-gray-700/20' : ''
                  } ${urgent ? 'border-l-2 border-red-500' : ''}`}
                  onClick={() => setExpandedEvent(isExpanded ? null : notification.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`p-2 rounded-lg ${style.bgColor} flex-shrink-0`}>
                      <EventIcon className={`w-4 h-4 text-${style.color}-400`} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium text-sm truncate">
                            {notification.title}
                          </span>
                          {getStatusIcon(notification)}
                          {urgent && (
                            <span className="text-xs px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full">
                              Urgent
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(notification.timestamp)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {isToday(notification.timestamp) ? 'Today' : formatDate(notification.timestamp)}
                        </span>
                        {notification.metadata?.aircraft && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Plane className="w-3 h-3" />
                            {notification.metadata.aircraft}
                          </span>
                        )}
                        {notification.metadata?.staff && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {notification.metadata.staff.split(' ')[0]}
                          </span>
                        )}
                      </div>
                      
                      {/* Progress bar */}
                      {notification.progress > 0 && (
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-gray-500 mb-0.5">
                            <span>Progress</span>
                            <span>{Math.round(notification.progress)}%</span>
                          </div>
                          <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 rounded-full transition-all duration-300"
                              style={{ width: `${notification.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-gray-600 space-y-2">
                          {notification.message && (
                            <p className="text-xs text-gray-400">{notification.message}</p>
                          )}
                          
                          <div className="flex flex-wrap gap-3 text-xs">
                            {notification.metadata?.startTime && (
                              <div className="flex items-center gap-1 text-gray-400">
                                <Clock className="w-3 h-3" />
                                <span>
                                  {new Date(notification.metadata.startTime).toLocaleString()} - 
                                  {notification.metadata.endTime ? new Date(notification.metadata.endTime).toLocaleTimeString() : ''}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Status tags */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {notification.metadata?.checkIn && (
                              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                                ✓ Check-in
                              </span>
                            )}
                            {notification.metadata?.checkOut && (
                              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                                ✓ Check-out
                              </span>
                            )}
                            {notification.metadata?.billingStatus === 'paid' && (
                              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                                ✓ Paid
                              </span>
                            )}
                            {notification.metadata?.billingStatus === 'pending' && (
                              <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full">
                                ⏳ Pending
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Expand icon */}
                    <ChevronRight 
                      className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-700 text-center">
          <button 
            onClick={navigateToSchedule}
            className="text-xs text-blue-400 hover:text-blue-300 flex items-center justify-center gap-1"
          >
            View full schedule
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default UpcomingEventsWidget;